import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, useTheme } from '@mui/material'

import { exportStudyMaterialsAuditReport } from '../../../utils/auditing-export-utilities'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import AuditPageStatsFilterBar from '../AuditPageStatsFilterBar'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import LineBarGraph from '../../common/LineBarGraph'
import CustomTable from '../../common/custom-table/CustomTable'
import { Icons } from '../../../utils/utilities'
import api from '../../../service/api'


const columns =
    [
        { id: 1, field: "index", index: true, name: 'Sl.No.', minWidth: "50px", align: "center", padding: "10px 20px" },
        { id: 2, field: "faculty_name", cellStyles: { textTransform: "capitalize" }, filterable: true, sortable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.auid}?legacy=${Boolean( row.legacy_system_material )}`, renderCell: row => row?.faculty_name?.toLowerCase() || row?.auid, name: "Faculty", minWidth: "150px", padding: "10px 20px" },
        { id: 3, filterable: true, sortable: true, field: "auid", linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.auid}?legacy=${Boolean( row.legacy_system_material )}`, name: "Emp ID", padding: "10px 20px", width: "120px" },
        { id: 7, field: "institute", filterable: true, name: "Institute", padding: "10px 20px" },
        { id: 7, field: "department", filterable: true, renderCell: row => `${row.department?.toUpperCase()}`, name: "Department", padding: "10px 20px" },
        { id: 5, dataType: "number", sortable: true, field: "material_count", align: "center", name: "Materials", minWidth: "150px" },
        { id: 6, dataType: "number", sortable: true, field: "attachments", align: "center", name: "Attachments", minWidth: "150px", padding: "10px 20px" },
        { id: 4, dataType: "number", sortable: true, field: "downloads", align: "center", name: "Downloads", minWidth: "150px", padding: "10px 20px" },
    ]

const StudyMaterialsStats = () => {

    const [searchParams] = useSearchParams( { sortByDept: "0", sortByAUID: "0", searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "0", sortByDownloadCounts: "0", sortByAttachmentCount: "0", sortByMaterialsCount: "0" } )
    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [loading, setLoading] = useState( true )
    const [graphData, setGraphData] = useState( [] )
    const [facultyStats, setFacultyStats] = useState( [] )
    const [loadingGraphData, setLoadingGraphData] = useState( true )
    const [last12MonthsStats, setLast12MonthsStats] = useState( null )
    const [error, setError] = useState( { graphDataError: null, statsError: null } )
    const [showzeromaterialsfaculties, setShowzeromaterialsfaculties] = useState( false )

    const { layout, palette } = useTheme()

    const filterFunc = item => {
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            const doesMatchDepartment = ( item.legacy_system_material && +legacy_department_id === +item.department_id ) ||
                ( !item.legacy_system_material && +department_id === +item.department_id )
            return doesMatchDepartment
        }
        return true
    }

    const getStats = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: { facultyStats, noMaterialsFacultiesList = [], last12MonthsStats = {} } } } = await api.audit.fetchStudyMaterialStats( { month, showzeromaterialsfaculties, institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            let alldata = facultyStats
            if ( showzeromaterialsfaculties ) {
                alldata = [...noMaterialsFacultiesList, ...facultyStats]
            }
            setFacultyStats( alldata )
            setLast12MonthsStats( Object.keys( last12MonthsStats || {} ).length > 0 ? last12MonthsStats : null )
            setError( prev => {
                const newValue = { ...prev }
                newValue.statsError = null
                return newValue
            } )
        } catch ( err ) {
            console.log( "STATTS ERR", err )
            setError( prev => {
                const newValue = { ...prev }
                newValue.statsError = { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the material audit data." }
                return newValue
            } )
        } finally {
            setLoading( false )
            setLoadingGraphData( false )
        }
    }, [month, selectedInstitute, showzeromaterialsfaculties] )

    useEffect( () => {
        getStats()
    }, [getStats] )

    useEffect( () => {
        const getGraphData = async () => {
            try {
                const institute = selectedInstitute !== "all" ? selectedInstitute : ""
                const department = selectedDepartment !== "all" ? selectedDepartment : ""
                const { data: graphDetails } = await api.audit.getStudyMaterialGraph( { month, institute, department } )
                setGraphData( graphDetails.data )
                setError( prev => {
                    const newValue = { ...prev }
                    newValue.graphDataError = null
                    return newValue
                } )
            } catch ( err ) {
                console.log( err )
                setError( prev => {
                    const newValue = { ...prev }
                    newValue.graphDataError = { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the material audit graph data." }
                    return newValue
                } )
            } finally {
                setLoadingGraphData( false )
            }
        }
        getGraphData()
    }, [month, selectedInstitute, selectedDepartment] )

    const filtered = facultyStats.filter( filterFunc )

    return (
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: layout.cardsGap }}>

            {/* Filters */}
            <AuditPageStatsFilterBar handleShowFacultyWithZeroResourceChange={e => setShowzeromaterialsfaculties( e.target.checked )} showzeroassignmentsfaculties={showzeromaterialsfaculties} resource="study materials" listResourceBtnLabel="View Materials" title="Study materials statistics" />


            {!loading && !error.statsError && view === 'tabular' && <CustomTable
                globalSearch="Search by faculty name or employee code..."
                pageSize={50}
                columns={columns}
                data={filtered}
                tableStyles={{
                    maxHeight: "450px"
                }}
                customActions={[
                    {
                        type: "button",
                        label: "Export",
                        startIcon: Icons.default.Publish,
                        onClick: ( rows ) => exportStudyMaterialsAuditReport( rows, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-meetings-audit-report` )
                    }
                ]}
            />}


            {error?.statsError && <TryAgainMessageBlock err={error?.statsError?.message} loading={loading} getData={getStats} code={error?.statsError?.code} />}

            {
                !loading && !loadingGraphData && view !== 'tabular' && filtered.length > 0 &&
                <Box display="flex" color={palette.common.font} flexGrow={1} minHeight="400px">
                    <LineBarGraph lineColor='#035efc' barColor='#FEE715' showMenuBar={false} yAxisName="Attachments" y2AxisName="Downloads" barData={graphData} barValuesKey='downloads' barLegend="Downloads" xAxisName="Date" lineLegend='Attachements uploaded' dateformat={"%Y-%m-%d"} xAxisValuesKey={'date'} lineValuesKey={'attachments_uploaded'} lineData={graphData} />
                </Box>
            }
            {( loading || loadingGraphData ) && !error.statsError && !error.graphDataError && <DataLoadingSpinner padding="0" waitingMessage="Loading materials audit report please..." />}
        </Box >
    )
}

export default StudyMaterialsStats
